import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/lib/auth-context";
import { observability, settings, signals, trades, telegram, trading, channels, exchanges, analytics, API } from "@/lib/api";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle, DialogClose,
} from "@/components/ui/dialog";
import { Badge, StatusPill, UptimeChip, MetricCard, MetricBreakdown, cn, Kv } from "@/components/atoms";
import { Field, ToggleRow, SliderRow } from "@/components/dashboard/formfields";
import SetupSection from "@/pages/setup/SetupSection";
import ChannelEditDialog, { PendingAiApprovalCard, ReparseStuckButton } from "@/pages/channels/ChannelEditDialog";
import ChannelsSection, { ExchangeSummaryCard } from "@/pages/channels/ChannelsSection";
import TradesSection, { PositionsTable, statusVariant, humanStatus } from "@/pages/trades/TradesSection";
import AuditSection from "@/pages/audit/AuditSection";
import SignalDiagnosticsSection from "@/pages/signals/SignalDiagnosticsSection";
import TradeDetailDialog from "@/pages/trades/TradeDetailDialog";
import PnLLeaderboard, { ChannelHeroGrid } from "@/pages/analytics/PnLLeaderboard";
import BacktestSection from "@/pages/backtest/BacktestSection";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Lightning, TelegramLogo, ChartLineUp, ChartLine, ShieldCheck, Gauge, ListBullets, Warning,
  Play, Pause, SignOut, Plugs, PlugsConnected, ArrowsClockwise, PaperPlaneTilt,
  ArrowUpRight, ArrowDownRight, Broadcast, MagnifyingGlass, CaretDown,
} from "@phosphor-icons/react";

const SAMPLE_MSG = `LONG BTC/USDT
Entry: 45000
SL: 44000
TP1: 46000
TP2: 47000
Leverage 10x`;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState("overview");
  // Which channel's detail page ChannelsSection should show (null = plain
  // list). Lifted up here, not owned by ChannelsSection, so both the
  // sidebar's nested channel list and Overview's hero cards can jump
  // straight into a channel's page from anywhere.
  const [viewingChannelId, setViewingChannelId] = useState(null);
  const [channelsNavOpen, setChannelsNavOpen] = useState(true);
  const goToChannel = (id) => { setViewingChannelId(id); setTab("channels"); };

  const overviewQ = useQuery({
    queryKey: ["overview"],
    queryFn: async () => (await observability.overview()).data,
    refetchInterval: 4000,
  });
  // Shared cache key with ChannelsSection's own channels query — powers the
  // sidebar's nested per-channel list without a second network round trip.
  const channelsNavQ = useQuery({
    queryKey: ["channels"],
    queryFn: async () => (await channels.list()).data,
    enabled: !!overviewQ.data?.multi_channel_enabled,
  });

  // Mark-to-market poll
  useEffect(() => {
    const iv = setInterval(() => trades.mark().catch(() => {}), 10000);
    return () => clearInterval(iv);
  }, []);

  const ov = overviewQ.data;

  return (
    <div className="min-h-screen text-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-slate-200 bg-white/80 backdrop-blur-xl backdrop-saturate-150">
        <div className="w-full px-4 sm:px-6 min-h-16 py-2 flex flex-wrap items-center justify-between gap-y-2">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#4F46E5] to-[#7C6CF0] flex items-center justify-center shrink-0">
              <Lightning size={18} weight="fill" color="white" />
            </div>
            <div className="flex flex-col">
              <span className="font-display text-lg leading-none">SIGNAL/DESK</span>
              <span className="hidden sm:block text-[10px] tracking-widest text-slate-500 uppercase mt-1">
                Telegram → WEEX Executor
              </span>
            </div>
          </div>

          <div className="flex items-center gap-1.5 flex-wrap justify-end">
            <StatusPill
              testId="badge-telegram"
              status={ov?.telegram_status === "connected" ? "connected" : ov?.telegram_status === "pending_code" ? "pending_code" : "error"}
              label={ov?.telegram_status === "connected" ? "TG" : "TG OFF"}
              title={`Telegram: ${(ov?.telegram_status || "disconnected").toUpperCase()}${ov?.uptime_24h?.telegram?.pct != null ? ` · ${ov.uptime_24h.telegram.pct}% 24h` : ""}`}
            />

            {/* Multi-exchange status badges — ONE compact pill per exchange
                collapsing (paper|live|status) × (auto_trading) into a single
                colour so the header stays readable on smaller viewports. */}
            {ov?.multi_channel_enabled ? (
              Object.entries(ov?.by_exchange || {}).length > 0 ? (
                Object.entries(ov.by_exchange).map(([ex, s]) => {
                  const connected = s.status === "connected" || s.status === "ok";
                  // State precedence: auto-off wins (RED), else paper (AMBER),
                  // else live+connected (GREEN), else live+error (RED).
                  let label, status, hint;
                  if (!s.auto_trading) {
                    label = "OFF";
                    status = "loss";
                    hint = "Auto-trading disabled";
                  } else if (s.paper_mode) {
                    label = "PAPER";
                    status = "paper";
                    hint = "Paper mode · signals executing simulated";
                  } else if (connected) {
                    label = "LIVE";
                    status = "active";
                    hint = "Live · auto-executing signals";
                  } else {
                    label = "ERR";
                    status = "error";
                    hint = s.status || "not configured";
                  }
                  return (
                    <StatusPill
                      key={ex}
                      testId={`badge-${ex}`}
                      status={status}
                      label={`${ex.toUpperCase()} ${label}`}
                      title={`${ex.toUpperCase()}: ${hint}`}
                    />
                  );
                })
              ) : (
                <StatusPill testId="badge-setup-needed" status="paper" label="SETUP →" />
              )
            ) : (
              <>
                <StatusPill
                  testId="badge-auto-trading"
                  status={ov?.auto_trading ? "active" : "loss"}
                  label={ov?.auto_trading ? "AUTO ON" : "AUTO OFF"}
                />
                <StatusPill
                  testId="badge-weex"
                  status={ov?.paper_mode ? "paper" : ov?.weex_status === "connected" ? "connected" : "error"}
                  label={ov?.paper_mode ? "WEEX PAPER" : `WEEX ${(ov?.weex_status || "").toUpperCase()}`}
                />
              </>
            )}
            {ov?.pending_ai_signals > 0 && (
              <StatusPill
                testId="badge-pending-approval"
                status="warn"
                label={`${ov.pending_ai_signals} AI PENDING`}
              />
            )}
            {ov?.kill_switch_active && (
              <StatusPill
                testId="badge-kill-switch"
                status="error"
                label="KILL SWITCH"
              />
            )}
            {/* Watchdog "TG DOWN" badge suppressed when the primary
                TELEGRAM/TG OFF pill already carries the same info. */}

            {ov?.app_env && ov.app_env !== "production" && (
              <span
                data-testid="badge-app-env"
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider border bg-amber-50 text-amber-700 border-amber-200"
              >
                {ov.app_env}
              </span>
            )}
            <Separator orientation="vertical" className="h-6 bg-slate-200 mx-1" />
            <EmergencyStopButton ov={ov} />
            <div className="flex items-center gap-2 text-xs">
              <div className="text-right hidden sm:block">
                <p className="text-slate-900 font-medium leading-none" data-testid="user-name">{user?.name || user?.email}</p>
                <p className="text-slate-500 leading-none mt-0.5">{user?.email}</p>
              </div>
              <Button
                data-testid="logout-btn"
                onClick={logout}
                size="sm"
                variant="ghost"
                className="text-slate-600 hover:text-slate-900 hover:bg-slate-100"
              >
                <SignOut size={16} />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="w-full px-4 sm:px-6 py-6 sm:py-8">
        <Tabs
          value={tab}
          onValueChange={setTab}
          orientation="vertical"
          className="w-full flex flex-col md:flex-row gap-6 items-start"
        >
          <div className="w-full md:w-60 shrink-0 md:sticky md:top-24 flex flex-col gap-3">
            <TabsList
              className="bg-white border border-slate-200 rounded-2xl shadow-sm flex md:flex-col justify-start w-full h-auto p-2 gap-1 overflow-x-auto md:overflow-visible flex-nowrap scrollbar-thin"
            >
              {/* Daily-use tabs first (dashboard, config, positions), then a
                  divider, then occasional-use diagnostic/evaluation tools —
                  keeps 7-8 equally-weighted tabs from reading as one flat,
                  undifferentiated list. Vertical sidebar on md+ so the label
                  is always visible; falls back to a horizontal scrollable
                  strip on small screens rather than wrapping/clipping. */}
              <span className="hidden md:block px-3 pt-1 pb-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest shrink-0">
                Main
              </span>
              <TabsTrigger
                value="overview"
                data-testid="tab-overview"
                className="md:w-full justify-start data-[state=active]:bg-[#4F46E5]/10 data-[state=active]:text-[#4F46E5] data-[state=active]:font-bold rounded-lg h-10 px-3 gap-3 text-slate-600 font-medium shrink-0"
              >
                <Gauge size={16} />
                <span>Overview</span>
              </TabsTrigger>

              {ov?.multi_channel_enabled && (
                <div className="flex flex-col gap-0.5 shrink-0">
                  <div className="flex items-center gap-0.5">
                    <TabsTrigger
                      value="channels"
                      data-testid="tab-channels"
                      onClick={() => setViewingChannelId(null)}
                      className="flex-1 md:w-full justify-start data-[state=active]:bg-[#4F46E5]/10 data-[state=active]:text-[#4F46E5] data-[state=active]:font-bold rounded-lg h-10 px-3 gap-3 text-slate-600 font-medium"
                    >
                      <Broadcast size={16} />
                      <span>Channels</span>
                    </TabsTrigger>
                    <button
                      type="button"
                      data-testid="channels-nav-toggle"
                      onClick={() => setChannelsNavOpen((v) => !v)}
                      className="hidden md:flex items-center justify-center w-7 h-10 text-slate-400 hover:text-slate-700 shrink-0"
                      aria-label={channelsNavOpen ? "Collapse channels list" : "Expand channels list"}
                    >
                      <CaretDown size={13} className={cn("transition-transform", !channelsNavOpen && "-rotate-90")} />
                    </button>
                  </div>
                  {channelsNavOpen && (
                    <div className="hidden md:flex flex-col gap-0.5 pl-8 pr-1">
                      <button
                        type="button"
                        data-testid="channels-nav-overview"
                        onClick={() => goToChannel(null)}
                        className={cn(
                          "text-left text-xs rounded-md px-2.5 py-1.5 font-semibold truncate",
                          tab === "channels" && !viewingChannelId
                            ? "text-[#4F46E5] bg-[#4F46E5]/10"
                            : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                        )}
                      >
                        Channel Overview
                      </button>
                      {(channelsNavQ.data || []).map((c) => (
                        <button
                          type="button"
                          key={c.id}
                          data-testid={`channels-nav-${c.id}`}
                          onClick={() => goToChannel(c.id)}
                          title={c.name}
                          className={cn(
                            "text-left text-xs rounded-md px-2.5 py-1.5 font-semibold truncate",
                            tab === "channels" && viewingChannelId === c.id
                              ? "text-[#4F46E5] bg-[#4F46E5]/10"
                              : "text-slate-500 hover:text-slate-800 hover:bg-slate-50"
                          )}
                        >
                          {c.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <TabsTrigger
                value="trades"
                data-testid="tab-trades"
                className="md:w-full justify-start data-[state=active]:bg-[#4F46E5]/10 data-[state=active]:text-[#4F46E5] data-[state=active]:font-bold rounded-lg h-10 px-3 gap-3 text-slate-600 font-medium shrink-0"
              >
                <ChartLineUp size={16} />
                <span>Trades</span>
              </TabsTrigger>
              <TabsTrigger
                value="setup"
                data-testid="tab-setup"
                className="md:w-full justify-start data-[state=active]:bg-[#4F46E5]/10 data-[state=active]:text-[#4F46E5] data-[state=active]:font-bold rounded-lg h-10 px-3 gap-3 text-slate-600 font-medium shrink-0"
              >
                <Plugs size={16} />
                <span>Settings</span>
              </TabsTrigger>

              <div className="flex md:flex-col items-center md:items-stretch gap-2 px-2 md:px-3 md:pt-3 self-stretch md:self-auto shrink-0" data-testid="tab-group-divider">
                <span className="w-px h-5 md:w-auto md:h-px md:bg-slate-200 bg-slate-200" />
                <span className="hidden md:inline text-[10px] font-bold text-slate-400 uppercase tracking-widest">Tools</span>
              </div>

              {[
                { v: "signals", label: "Message Log", icon: <PaperPlaneTilt size={16} /> },
                { v: "events", label: "Activity Log", icon: <ListBullets size={16} /> },
                { v: "diagnostics", label: "Signal Diagnostics", icon: <MagnifyingGlass size={16} /> },
                { v: "audit", label: "Channel Analyzer", icon: <Warning size={16} /> },
                { v: "backtest", label: "Backtest", icon: <ChartLine size={16} /> },
              ].map((t) => (
                <TabsTrigger
                  key={t.v}
                  value={t.v}
                  data-testid={`tab-${t.v}`}
                  className="md:w-full justify-start data-[state=active]:bg-[#4F46E5]/10 data-[state=active]:text-[#4F46E5] data-[state=active]:font-bold rounded-lg h-10 px-3 gap-3 text-slate-500 font-medium shrink-0"
                >
                  {t.icon}
                  <span>{t.label}</span>
                </TabsTrigger>
              ))}
            </TabsList>

            {/* Real kill-switch status, mirroring the header badge — pinned
                under the nav so it's visible without scrolling, matching the
                design-canvas mockup's sidebar footer card. */}
            <div
              className={cn(
                "hidden md:block rounded-2xl p-3.5",
                ov?.kill_switch_active ? "bg-red-50 border border-red-200" : "bg-[#4F46E5]/5"
              )}
              data-testid="sidebar-kill-switch-card"
            >
              <p className={cn("text-xs font-bold", ov?.kill_switch_active ? "text-red-700" : "text-[#4F46E5]")}>
                Kill switch
              </p>
              <p className="text-xs text-slate-500 mt-1 leading-relaxed">
                {ov?.kill_switch_active
                  ? `Tripped${ov.kill_switch_reason ? ` — ${ov.kill_switch_reason}` : ""}`
                  : "All channels armed · no active trips"}
              </p>
            </div>
          </div>

          <div className="flex-1 min-w-0 w-full">
            <TabsContent value="overview" className="mt-0">
              <OverviewSection ov={ov} onSelectChannel={goToChannel} />
            </TabsContent>
            <TabsContent value="setup" className="mt-0"><SetupSection /></TabsContent>
            {ov?.multi_channel_enabled && (
              <TabsContent value="channels" className="mt-0">
                <ChannelsSection
                  viewingChannelId={viewingChannelId}
                  onSelectChannel={setViewingChannelId}
                  onBack={() => setViewingChannelId(null)}
                />
              </TabsContent>
            )}
            <TabsContent value="trades" className="mt-0"><TradesSection /></TabsContent>
            <TabsContent value="signals" className="mt-0"><SignalsSection /></TabsContent>
            <TabsContent value="events" className="mt-0"><EventsSection /></TabsContent>
            <TabsContent value="diagnostics" className="mt-0"><SignalDiagnosticsSection /></TabsContent>
            <TabsContent value="audit" className="mt-0"><AuditSection /></TabsContent>
            <TabsContent value="backtest" className="mt-0"><BacktestSection /></TabsContent>
          </div>
        </Tabs>
      </main>
    </div>
  );
}

/* ============ OVERVIEW ============ */
function EmergencyStopButton({ ov }) {
  const qc = useQueryClient();
  const openCount = ov?.open_trades_count ?? 0;
  const [open, setOpen] = useState(false);
  const stop = useMutation({
    mutationFn: () => trading.emergencyStop(),
    onSuccess: (r) => {
      const { closed_count, failed_count } = r.data;
      if (failed_count > 0) {
        toast.warning(`Closed ${closed_count} · ${failed_count} failed. Check Events.`);
      } else {
        toast.success(`Emergency stop: ${closed_count} position${closed_count !== 1 ? "s" : ""} closed. Auto-trading OFF.`);
      }
      qc.invalidateQueries();
      setOpen(false);
    },
    onError: (e) => toast.error("Emergency stop failed: " + (e.response?.data?.detail || e.message)),
  });
  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          data-testid="emergency-stop-btn"
          size="sm"
          className="bg-red-600 hover:bg-red-700 text-white font-medium shadow-sm border border-red-700"
        >
          🛑 Emergency Stop
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid="emergency-stop-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-red-700">Close ALL positions and halt trading?</AlertDialogTitle>
          <AlertDialogDescription>
            This will immediately:
            <ul className="list-disc pl-5 mt-2 space-y-1 text-slate-700 text-sm">
              <li><strong>Close {openCount} open position{openCount !== 1 ? "s" : ""}</strong> at market price</li>
              <li>Cancel any pending WEEX stop-loss orders</li>
              <li>Turn OFF auto-trading (activates kill switch)</li>
              <li>Send a Telegram alert to your Saved Messages</li>
            </ul>
            <p className="mt-3 text-red-700 text-sm">This cannot be undone. You&apos;ll need to re-enable Auto-trading manually.</p>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid="emergency-cancel-btn">Cancel</AlertDialogCancel>
          <AlertDialogAction
            data-testid="emergency-confirm-btn"
            onClick={(e) => { e.preventDefault(); stop.mutate(); }}
            disabled={stop.isPending}
            className="bg-red-600 hover:bg-red-700"
          >
            {stop.isPending ? "Closing..." : `Yes, close ${openCount} and halt`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function KillSwitchBanner({ ov }) {
  const qc = useQueryClient();
  const reset = useMutation({
    mutationFn: () => settings.resetKillSwitch(),
    onSuccess: () => {
      toast.success("Kill switch cleared. Turn Auto-trading back on when you're ready.");
      qc.invalidateQueries();
    },
    onError: (e) => toast.error("Reset failed: " + e.message),
  });
  const when = ov?.kill_switch_at ? new Date(ov.kill_switch_at).toLocaleString() : "—";
  return (
    <div className="lg:col-span-4 card-surface p-5 border-2 border-red-400 bg-red-50/60 flex items-start gap-4"
         data-testid="kill-switch-banner">
      <div className="text-2xl">🛑</div>
      <div className="flex-1">
        <p className="overline text-red-700">Auto-trading halted by circuit breaker</p>
        <p className="text-sm text-slate-800 mt-1">
          <strong>Reason:</strong> {ov.kill_switch_reason || "unknown"}
        </p>
        <p className="text-xs text-slate-500 mt-1">Triggered at {when}. Existing positions are untouched — no new trades will execute until you clear this and re-enable Auto-trading.</p>
      </div>
      <Button
        data-testid="reset-kill-switch-btn"
        onClick={() => reset.mutate()}
        disabled={reset.isPending}
        className="bg-red-600 hover:bg-red-700 text-white"
      >
        Clear kill switch
      </Button>
    </div>
  );
}

function OverviewSection({ ov, onSelectChannel }) {
  const [text, setText] = useState(SAMPLE_MSG);
  const [targetChannelId, setTargetChannelId] = useState("");
  const qc = useQueryClient();

  const channelsQ = useQuery({
    queryKey: ["channels"],
    queryFn: async () => (await channels.list()).data,
    refetchInterval: 8000,
  });

  const toggleChannel = useMutation({
    mutationFn: ({ id, enabled }) => channels.update(id, { enabled }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ["channels"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      toast[vars.enabled ? "success" : "info"](
        vars.enabled ? "Channel enabled" : "Channel paused"
      );
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Toggle failed"),
  });

  const ingest = useMutation({
    mutationFn: (t) => signals.ingest(t, "manual", targetChannelId || undefined),
    onSuccess: (res) => {
      const d = res.data;
      const eng = d.engine?.action;
      toast.success(`Signal parsed (${d.parsed.action_type}) — engine: ${eng}`);
      qc.invalidateQueries();
    },
    onError: (e) => toast.error("Ingest failed: " + e.message),
  });

  const openTradesQ = useQuery({
    queryKey: ["trades", "open"],
    queryFn: async () => (await trades.list("open")).data,
    refetchInterval: 4000,
  });

  const allTradesQ = useQuery({
    queryKey: ["trades", "all"],
    queryFn: async () => (await trades.list()).data,
    refetchInterval: 15000,
  });
  const pnlQ = useQuery({
    queryKey: ["per-channel-pnl"],
    queryFn: async () => (await analytics.perChannelPnl()).data,
    refetchInterval: 15000,
  });
  const rawQ = useQuery({
    queryKey: ["raw"],
    queryFn: async () => (await signals.listRaw()).data,
    refetchInterval: 15000,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl">Overview</h2>
          <p className="text-sm text-slate-500 mt-0.5">
            {channelsQ.data?.length ?? 0} channel{(channelsQ.data?.length ?? 0) === 1 ? "" : "s"}
            {ov?.balance != null && ` · $${Number(ov.balance).toLocaleString(undefined, { maximumFractionDigits: 2 })} combined equity`}
          </p>
        </div>
      </div>

      <SystemStatusCard ov={ov} />

    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      {ov?.kill_switch_active && <KillSwitchBanner ov={ov} />}
      <MetricCard
        testId="metric-balance"
        label="Effective Balance"
        value={ov ? `$${Number(ov.balance).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}
        sub={(() => {
          const exNames = (ov?.balance_exchanges || [])
            .map((e) => e.charAt(0).toUpperCase() + e.slice(1))
            .join(" + ");
          if (ov?.balance_source === "paper") return "Paper mode";
          if (ov?.balance_source === "live") return exNames ? `Live · ${exNames}` : "Live";
          if (ov?.balance_source === "live_stale") return "Live · fetch failed (cached)";
          if (ov?.balance_source === "unavailable") return "Live · unavailable (check keys)";
          return ov?.paper_mode ? "Paper mode" : "Live";
        })()}
        tone={ov && !ov.paper_mode && ov.balance_source !== "live" ? "warn" : "neutral"}
      >
        <MetricBreakdown
          items={Object.entries(ov?.by_exchange || {}).map(([ex, d]) => ({
            label: ex,
            testId: `metric-balance-${ex}`,
            value: d.paper_mode ? "Paper" : d.balance_usdt != null ? `$${Number(d.balance_usdt).toFixed(2)}` : "—",
          }))}
        />
      </MetricCard>
      <MetricCard
        testId="metric-realized"
        label="Realized PnL"
        value={ov ? `$${Number(ov.realized_pnl).toFixed(2)}` : "—"}
        tone={ov?.realized_pnl > 0 ? "profit" : ov?.realized_pnl < 0 ? "loss" : "neutral"}
        sub={`${ov?.closed_trades_count ?? 0} closed`}
      >
        <MetricBreakdown
          items={Object.entries(ov?.by_exchange || {}).map(([ex, d]) => ({
            label: ex,
            testId: `metric-realized-${ex}`,
            value: `$${Number(d.realized_pnl_alltime ?? 0).toFixed(2)}`,
            tone: d.realized_pnl_alltime > 0 ? "profit" : d.realized_pnl_alltime < 0 ? "loss" : "neutral",
          }))}
        />
      </MetricCard>
      <MetricCard
        testId="metric-unrealized"
        label="Unrealized PnL"
        value={ov ? `$${Number(ov.unrealized_pnl).toFixed(2)}` : "—"}
        tone={ov?.unrealized_pnl > 0 ? "profit" : ov?.unrealized_pnl < 0 ? "loss" : "neutral"}
        sub={`${ov?.open_trades_count ?? 0} open`}
      >
        <MetricBreakdown
          items={Object.entries(ov?.by_exchange || {}).map(([ex, d]) => ({
            label: ex,
            testId: `metric-unrealized-${ex}`,
            value: `$${Number(d.unrealized_pnl ?? 0).toFixed(2)}`,
            tone: d.unrealized_pnl > 0 ? "profit" : d.unrealized_pnl < 0 ? "loss" : "neutral",
          }))}
        />
      </MetricCard>
      <MetricCard
        testId="metric-protected"
        label="Protected by Internal SL"
        value={ov?.protected_by_internal_sl_count ?? 0}
        tone="warn"
        sub="Waiting for provider SL"
      />

      {/* Per-channel gradient hero cards — same real per_channel_pnl data as
          the leaderboard table below, surfaced as the headline visual. */}
      {ov?.multi_channel_enabled && (
        <div className="lg:col-span-4">
          <ChannelHeroGrid onSelectChannel={onSelectChannel} />
        </div>
      )}

      {/* Per-channel P&L leaderboard — imported but never actually rendered
          here (dead import left behind by an earlier edit); restored
          2026-08-21 to its original position, right after the top metric
          cards and before the channel toggle strip. */}
      {ov?.multi_channel_enabled && (
        <div className="lg:col-span-4">
          <PnLLeaderboard />
        </div>
      )}

      {ov?.multi_channel_enabled && (
        <div className="lg:col-span-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2">
            <PortfolioPerformanceCard trades={allTradesQ.data} channels={channelsQ.data} />
          </div>
          <SignalHealthCard pnl={pnlQ.data} ov={ov} raw={rawQ.data} />
        </div>
      )}

      {ov?.multi_channel_enabled && (
        <div className="lg:col-span-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <BalanceByExchangeCard ov={ov} />
          <TradesByChannelCard pnl={pnlQ.data} />
        </div>
      )}

      {/* Per-channel enable/pause strip — mirrors the Channels tab but keeps
          the master switches one click away from the trading dashboard. */}
      {ov?.multi_channel_enabled && channelsQ.data && channelsQ.data.length > 0 && (
        <div className="lg:col-span-4 card-surface p-6" data-testid="channels-strip">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="overline">Channels</p>
              <p className="text-xs text-slate-500 mt-1">
                Per-channel on/off. Pausing a channel keeps its config but stops execution — signals still parse and log.
              </p>
            </div>
            <p className="text-[11px] text-slate-500 mono">
              {channelsQ.data.filter((c) => c.enabled).length} of {channelsQ.data.length} active
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {channelsQ.data.map((ch) => {
              const isKilled = !!ch.kill_switch_active;
              return (
                <div key={ch.id}
                     data-testid={`ov-channel-row-${ch.id}`}
                     className={cn(
                       "flex items-center justify-between rounded-2xl border p-4 transition-colors",
                       isKilled
                         ? "bg-[#FDECEC] border-red-200"
                         : ch.enabled
                         ? "bg-[#E7F7F1] border-emerald-200"
                         : "bg-slate-50 border-slate-200"
                     )}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <div className={cn(
                        "w-2 h-2 rounded-full shrink-0",
                        isKilled ? "bg-red-500" : ch.enabled ? "bg-emerald-500 animate-pulse" : "bg-slate-400"
                      )} />
                      <p className="text-sm font-medium truncate">{ch.name}</p>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-0.5 truncate">
                      <span className="uppercase mono">{ch.exchange}</span>
                      {ch.telegram_chat_label && (
                        <> · 📡 {ch.telegram_chat_label}</>
                      )}
                      {isKilled && <span className="text-red-600 ml-1">· KILLED</span>}
                    </p>
                  </div>
                  <Switch
                    checked={!!ch.enabled}
                    disabled={isKilled || toggleChannel.isPending}
                    onCheckedChange={(v) => toggleChannel.mutate({ id: ch.id, enabled: v })}
                    data-testid={`ov-channel-switch-${ch.id}`}
                    className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-400 shrink-0"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Per-exchange summary — surfaced high on the page since it's the
          primary "how am I doing per venue" view. Was previously below the
          injector; moved up per user request "where is the details for each
          exchange". */}
      {ov?.multi_channel_enabled && ov?.by_exchange && Object.keys(ov.by_exchange).length > 0 && (
        <div className="lg:col-span-4 card-surface p-6" data-testid="by-exchange-strip">
          <div className="flex items-center justify-between mb-3">
            <div>
              <p className="overline">Per-exchange details</p>
              <p className="text-xs text-slate-500 mt-1">
                Balance, open positions and 24h realized PnL for each configured venue.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {Object.entries(ov.by_exchange).map(([ex, s]) => (
              <ExchangeSummaryCard key={ex} exchange={ex} summary={s} />
            ))}
          </div>
        </div>
      )}

      {/* Signal injector — kept as a manual test tool; per-channel routing lives in Channels tab */}
      <div className="card-surface p-6 lg:col-span-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <p className="overline">Test Signal Injector</p>
            <p className="text-xs text-slate-500 mt-1">
              Paste a signal to run it through the parser + engine — useful for sanity-checking parser changes without waiting for a live message.
            </p>
          </div>
        </div>
        <Textarea
          data-testid="signal-injector-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={4}
          className="bg-white border-slate-200 mono text-sm resize-none"
        />
        <div className="mt-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
          {channelsQ.data && channelsQ.data.length > 0 && (
            <div className="flex-1 min-w-0">
              <Label className="text-[11px] text-slate-500 mb-1 block">
                Target channel (which risk profile + exchange to route through)
              </Label>
              <Select
                value={targetChannelId || "__auto__"}
                onValueChange={(v) => setTargetChannelId(v === "__auto__" ? "" : v)}
              >
                <SelectTrigger
                  data-testid="signal-injector-target-channel"
                  className="w-full h-10 bg-white"
                >
                  <SelectValue placeholder="Auto-route by chat_id (default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__auto__" data-testid="signal-injector-target-auto">
                    Auto-route (chat_id match, else single-channel fallback)
                  </SelectItem>
                  {channelsQ.data.map((ch) => (
                    <SelectItem key={ch.id} value={ch.id}
                                data-testid={`signal-injector-target-${ch.id}`}>
                      {ch.name} · {ch.exchange.toUpperCase()}{ch.enabled ? "" : " (paused)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button
            data-testid="signal-injector-submit"
            onClick={() => ingest.mutate(text)}
            disabled={ingest.isPending || !text.trim()}
            className="bg-[#4F46E5] hover:bg-[#4338CA] sm:self-end"
          >
            <PaperPlaneTilt size={16} className="mr-2" />
            {ingest.isPending ? "Processing…" : "Inject signal"}
          </Button>
        </div>
      </div>

      {/* Live open positions */}
      <div className="card-surface p-6 lg:col-span-4" data-testid="live-positions">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="overline">Live open positions</p>
            <h3 className="font-display text-xl mt-1">Trade Monitor</h3>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => qc.invalidateQueries({ queryKey: ["trades"] })}
            data-testid="refresh-trades"
          >
            <ArrowsClockwise size={16} className="mr-2" /> Refresh
          </Button>
        </div>
        <PositionsTable trades={(openTradesQ.data || []).filter((t) => !t.paper_mode && t.status !== "pending_limit")} />
      </div>

      {ov?.multi_channel_enabled && (
        <div className="lg:col-span-4">
          <RecentTradesCard trades={allTradesQ.data} channels={channelsQ.data} />
        </div>
      )}

      {/* Health watchdog footer */}
      {ov?.health && ov.health.last_check_at && (
        <div className="lg:col-span-4 text-[10px] text-slate-400 flex items-center justify-end gap-3 -mt-2"
             data-testid="watchdog-footer">
          <span className="flex items-center gap-1">
            <span className={cn("inline-block w-2 h-2 rounded-full",
              ov.health.telegram === "up" ? "bg-emerald-500" : ov.health.telegram === "down" ? "bg-red-500" : "bg-slate-300"
            )} />
            Telegram
          </span>
          <span className="flex items-center gap-1">
            <span className={cn("inline-block w-2 h-2 rounded-full",
              ov.health.weex === "up" ? "bg-emerald-500" : ov.health.weex === "down" ? "bg-red-500" : "bg-slate-300"
            )} />
            WEEX
          </span>
          <span className="mono">watchdog · last check {new Date(ov.health.last_check_at).toLocaleTimeString()}</span>
        </div>
      )}
    </div>
    </div>
  );
}

/* Distinct connection-status card - separate from the header pills, matching
   the mockup's dedicated "System Status" strip. Same underlying real status
   data the header badges already read (ov.telegram_status / ov.by_exchange),
   just surfaced a second time as its own labeled row. */
function SystemStatusCard({ ov }) {
  const exEntries = Object.entries(ov?.by_exchange || {});
  return (
    <div className="card-surface p-4 flex items-center gap-6 flex-wrap" data-testid="system-status-card">
      <p className="overline shrink-0">System Status</p>
      <span className="flex items-center gap-2 text-sm">
        <span className={cn("w-2 h-2 rounded-full", ov?.telegram_status === "connected" ? "bg-emerald-500" : "bg-red-500")} />
        <span className="font-bold">Telegram</span>
        <span className="text-slate-500">{ov?.telegram_status === "connected" ? "Connected" : (ov?.telegram_status || "disconnected")}</span>
      </span>
      {exEntries.length > 0 && <div className="w-px h-4 bg-slate-200" />}
      {exEntries.map(([ex, s]) => {
        const connected = s.status === "connected" || s.status === "ok";
        return (
          <span key={ex} className="flex items-center gap-2 text-sm">
            <span className={cn("w-2 h-2 rounded-full", connected ? "bg-emerald-500" : "bg-red-500")} />
            <span className="font-bold uppercase">{ex}</span>
            <span className="text-slate-500">{connected ? `Connected${s.paper_mode ? " · paper" : ""}` : (s.status || "not configured")}</span>
          </span>
        );
      })}
    </div>
  );
}

const CHART_COLORS = ["#4F46E5", "#0F766E", "#C2410C", "#6D28D9", "#BE185D", "#1D4ED8"];
const OV_CLOSED_STATUSES = ["closed_by_signal", "closed_by_tp", "closed_by_sl", "closed_by_auto_roi"];

/* Real compounded-PnL curve per channel — cumulative sum of realized_pnl
   across each channel's own real (non-paper) closed trades, in fill order.
   This is an honest substitute for the mockup's "$100 start" equity curve:
   we don't have a real historical starting balance to compound from, so we
   chart the real dollar path instead of a fabricated indexed return. */
function PortfolioPerformanceCard({ trades: allTrades, channels: chList }) {
  const chById = React.useMemo(() => {
    const m = {};
    for (const c of chList || []) m[c.id] = c;
    return m;
  }, [chList]);

  const { data, seriesKeys } = React.useMemo(() => {
    const closed = (allTrades || [])
      .filter((t) => !t.paper_mode && t.closed_at && OV_CLOSED_STATUSES.includes(t.status))
      .sort((a, b) => new Date(a.closed_at) - new Date(b.closed_at));
    const running = {};
    const points = [];
    for (const t of closed) {
      const key = chById[t.channel_id]?.name || "Unrouted";
      running[key] = (running[key] || 0) + (t.realized_pnl || 0);
      points.push({ ts: new Date(t.closed_at).getTime(), [key]: running[key] });
    }
    // Forward-fill each series so lines don't drop to zero between a
    // channel's own trades - each point only carries the channel(s) that
    // just changed, so downstream consumers must carry the rest forward.
    const keys = Array.from(new Set(points.flatMap((p) => Object.keys(p).filter((k) => k !== "ts"))));
    const last = {};
    const filled = points.map((p) => {
      Object.assign(last, p);
      return { ts: p.ts, ...keys.reduce((acc, k) => ({ ...acc, [k]: last[k] ?? 0 }), {}) };
    });
    return { data: filled, seriesKeys: keys };
  }, [allTrades, chById]);

  return (
    <div className="card-surface p-6 h-full" data-testid="portfolio-performance-card">
      <p className="overline">Compounded realized P&amp;L</p>
      <h3 className="font-display text-xl mt-1">Portfolio Performance</h3>
      <p className="text-xs text-slate-500 mt-1">Cumulative real $ P&amp;L per channel, in trade-close order</p>
      {data.length > 1 ? (
        <div className="h-56 mt-3">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="ts" tickFormatter={(v) => new Date(v).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                     tick={{ fill: "#94a3b8", fontSize: 10 }} stroke="#e2e8f0" />
              <YAxis tick={{ fill: "#94a3b8", fontSize: 10 }} tickFormatter={(v) => `$${v}`} stroke="#e2e8f0" />
              <Tooltip labelFormatter={(v) => new Date(v).toLocaleString()}
                       formatter={(v) => (typeof v === "number" ? `$${v.toFixed(2)}` : v)}
                       contentStyle={{ background: "#14162B", border: "none", color: "#fff", fontSize: 11, borderRadius: 8 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              {seriesKeys.map((k, i) => (
                <Line key={k} type="monotone" dataKey={k} stroke={CHART_COLORS[i % CHART_COLORS.length]}
                      strokeWidth={2} dot={false} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <p className="text-sm text-slate-500 py-10 text-center">Not enough closed trades yet to chart a curve.</p>
      )}
    </div>
  );
}

function SignalHealthCard({ pnl, ov, raw }) {
  const wins = (pnl || []).reduce((s, r) => s + (r.wins || 0), 0);
  const losses = (pnl || []).reduce((s, r) => s + (r.losses || 0), 0);
  const winRate = wins + losses > 0 ? (wins / (wins + losses)) * 100 : null;
  const today = new Date().toISOString().slice(0, 10);
  const todayMsgs = (raw || []).filter((m) => (m.received_at || m.created_at || "").slice(0, 10) === today);
  const signalsToday = todayMsgs.length;
  const unparsedToday = todayMsgs.filter((m) => !m.parsed).length;

  const r = winRate != null ? winRate : 0;
  const circumference = 2 * Math.PI * 36;
  const dashOffset = circumference * (1 - r / 100);

  return (
    <div className="card-surface p-6 h-full flex flex-col gap-4" data-testid="signal-health-card">
      <h3 className="font-display text-xl">Signal Health</h3>
      <div className="flex items-center gap-4">
        <svg width="86" height="86" viewBox="0 0 86 86" className="shrink-0">
          <circle cx="43" cy="43" r="36" fill="none" stroke="#E7E9F2" strokeWidth="9" />
          {winRate != null && (
            <circle cx="43" cy="43" r="36" fill="none" stroke="#059669" strokeWidth="9" strokeLinecap="round"
                    strokeDasharray={circumference} strokeDashoffset={dashOffset}
                    transform="rotate(-90 43 43)" />
          )}
        </svg>
        <div>
          <p className="mono text-2xl font-bold">{winRate != null ? `${winRate.toFixed(1)}%` : "—"}</p>
          <p className="text-xs text-slate-500">Blended win rate (real trades)</p>
        </div>
      </div>
      <div className="h-px bg-slate-100" />
      <div className="flex flex-col gap-2.5 text-sm">
        <div className="flex justify-between"><span className="text-slate-500">Open positions</span><span className="mono font-bold">{ov?.open_trades_count ?? "—"}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Messages today</span><span className="mono font-bold">{signalsToday}</span></div>
        <div className="flex justify-between"><span className="text-slate-500">Unparsed today</span><span className="mono font-bold text-amber-600">{unparsedToday}</span></div>
      </div>
    </div>
  );
}

function BalanceByExchangeCard({ ov }) {
  const entries = Object.entries(ov?.by_exchange || {});
  const max = Math.max(1, ...entries.map(([, s]) => s.paper_mode ? 0 : Number(s.balance_usdt || 0)));
  return (
    <div className="card-surface p-6" data-testid="balance-by-exchange-card">
      <p className="overline">Live balance</p>
      <h3 className="font-display text-xl mt-1">Balance by Exchange</h3>
      <div className="flex flex-col gap-5 mt-5">
        {entries.map(([ex, s], i) => {
          const pct = s.paper_mode ? 100 : Math.max(2, (Number(s.balance_usdt || 0) / max) * 100);
          return (
            <div key={ex}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="text-sm font-bold uppercase">{ex}</span>
                  <Badge variant={s.paper_mode ? "warn" : "profit"}>{s.paper_mode ? "Paper" : "Live"}</Badge>
                </div>
                <span className="mono text-sm font-bold">
                  {s.paper_mode ? "—" : `$${Number(s.balance_usdt || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`}
                </span>
              </div>
              <div className="h-2.5 rounded-full bg-[#EEF0FF] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${pct}%`, background: CHART_COLORS[i % CHART_COLORS.length] }} />
              </div>
            </div>
          );
        })}
        {entries.length === 0 && <p className="text-sm text-slate-500 text-center py-4">No exchanges configured.</p>}
      </div>
    </div>
  );
}

function TradesByChannelCard({ pnl }) {
  const rows = pnl || [];
  const max = Math.max(1, ...rows.map((r) => (r.wins || 0) + (r.losses || 0)));
  return (
    <div className="card-surface p-6" data-testid="trades-by-channel-card">
      <div className="flex items-center justify-between">
        <div>
          <p className="overline">Closed trades</p>
          <h3 className="font-display text-xl mt-1">Trades by Channel</h3>
        </div>
        <div className="flex gap-3 text-xs text-slate-500">
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />Win</span>
          <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-500" />Loss</span>
        </div>
      </div>
      <div className="flex items-end justify-around gap-3 mt-6" style={{ height: 170 }}>
        {rows.map((r) => (
          <div key={r.channel_id} className="flex flex-col items-center gap-2">
            <div className="flex items-end gap-1" style={{ height: 140 }}>
              <div className="w-5 rounded-t bg-emerald-500 relative flex items-start justify-center"
                   style={{ height: `${Math.max(2, ((r.wins || 0) / max) * 140)}px` }}>
                <span className="mono text-[11px] font-bold text-emerald-600 absolute -top-5">{r.wins || 0}</span>
              </div>
              <div className="w-5 rounded-t bg-red-500 relative flex items-start justify-center"
                   style={{ height: `${Math.max(2, ((r.losses || 0) / max) * 140)}px` }}>
                <span className="mono text-[11px] font-bold text-red-600 absolute -top-5">{r.losses || 0}</span>
              </div>
            </div>
            <p className="text-[11px] font-bold text-center leading-tight max-w-[70px]">{r.channel_name}</p>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-slate-500">No closed trades yet.</p>}
      </div>
    </div>
  );
}

function RecentTradesCard({ trades: allTrades, channels: chList }) {
  const chById = React.useMemo(() => {
    const m = {};
    for (const c of chList || []) m[c.id] = c;
    return m;
  }, [chList]);

  const recent = React.useMemo(() => {
    return (allTrades || [])
      .filter((t) => !t.paper_mode)
      .sort((a, b) => new Date(b.closed_at || b.created_at) - new Date(a.closed_at || a.created_at))
      .slice(0, 8);
  }, [allTrades]);

  return (
    <div className="card-surface p-0 overflow-hidden" data-testid="recent-trades-card">
      <div className="flex items-center justify-between p-6 pb-3">
        <div>
          <p className="overline">All channels</p>
          <h3 className="font-display text-xl mt-1">Recent Trades</h3>
        </div>
        <Button
          onClick={() => window.open(trades.csvUrl(), "_blank")}
          variant="outline" size="sm" className="border-slate-300"
          data-testid="recent-trades-csv"
        >
          <ListBullets size={16} className="mr-2" /> Download CSV
        </Button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-widest text-slate-500 border-t border-b border-slate-200">
            <tr>
              <th className="text-left py-2.5 px-6">Symbol</th>
              <th className="text-left py-2.5 px-2">Channel</th>
              <th className="text-left py-2.5 px-2">Side</th>
              <th className="text-right py-2.5 px-2">Entry</th>
              <th className="text-right py-2.5 px-2">Exit</th>
              <th className="text-right py-2.5 px-2">PnL</th>
              <th className="text-left py-2.5 px-6">Status</th>
            </tr>
          </thead>
          <tbody>
            {recent.map((t) => {
              const isClosed = OV_CLOSED_STATUSES.includes(t.status);
              return (
                <tr key={t.id} className="border-b border-slate-100 last:border-0">
                  <td className="py-3 px-6 font-semibold">{t.symbol}</td>
                  <td className="py-3 px-2 text-slate-600">{chById[t.channel_id]?.name || "—"}</td>
                  <td className="py-3 px-2">
                    <Badge variant={t.side === "long" ? "profit" : "loss"}>{t.side}</Badge>
                  </td>
                  <td className="py-3 px-2 mono text-right">{t.entry_price}</td>
                  <td className="py-3 px-2 mono text-right">{isClosed ? (t.close_price ?? "—") : "—"}</td>
                  <td className={cn("py-3 px-2 mono text-right font-bold",
                    isClosed ? ((t.realized_pnl || 0) > 0 ? "text-emerald-600" : (t.realized_pnl || 0) < 0 ? "text-red-600" : "") : "text-slate-500")}>
                    {isClosed ? `${t.realized_pnl >= 0 ? "+" : "-"}$${Math.abs(t.realized_pnl || 0).toFixed(2)}`
                      : `${(t.unrealized_pnl || 0) >= 0 ? "+" : ""}${(t.unrealized_pnl || 0).toFixed(2)} unrl.`}
                  </td>
                  <td className="py-3 px-6"><Badge variant={statusVariant(t.status)}>{humanStatus(t.status)}</Badge></td>
                </tr>
              );
            })}
            {recent.length === 0 && (
              <tr><td colSpan={7} className="py-8 text-center text-slate-500">No trades yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SignalsSection() {
  const raw = useQuery({ queryKey: ["raw"], queryFn: async () => (await signals.listRaw()).data, refetchInterval: 4000 });
  const parsed = useQuery({ queryKey: ["parsed"], queryFn: async () => (await signals.listParsed()).data, refetchInterval: 4000 });
  const channelsQ = useQuery({ queryKey: ["channels"], queryFn: async () => (await channels.list()).data, staleTime: 10_000 });
  const [channelFilter, setChannelFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");

  const chById = React.useMemo(() => {
    const m = {};
    for (const c of channelsQ.data || []) m[c.id] = c;
    return m;
  }, [channelsQ.data]);

  // Live ingestion (both real-time Telegram and the X/GVRCALLS scraper)
  // writes channel_id directly onto the raw message. Only historical
  // backfilled messages may lack it - for those, fall back to whatever
  // channel_id the signal parsed from them landed on.
  const channelIdByRawMsgId = React.useMemo(() => {
    const m = {};
    for (const p of parsed.data || []) if (p.raw_message_id && p.channel_id) m[p.raw_message_id] = p.channel_id;
    return m;
  }, [parsed.data]);
  const resolveChannelId = (m) => m.channel_id || channelIdByRawMsgId[m.id];

  const usedChannelIds = React.useMemo(() => {
    const s = new Set();
    for (const m of raw.data || []) { const cid = m.channel_id || channelIdByRawMsgId[m.id]; if (cid) s.add(cid); }
    return Array.from(s);
  }, [raw.data, channelIdByRawMsgId]);

  const bySource = (m) => sourceFilter === "all" ? true
    : sourceFilter === "x" ? m.source === "x_subscriber_scrape"
    : m.source !== "x_subscriber_scrape";

  const visibleRaw = (raw.data || [])
    .filter((m) => channelFilter === "all" || resolveChannelId(m) === channelFilter)
    .filter(bySource);
  const visibleParsed = channelFilter === "all" ? (parsed.data || [])
    : (parsed.data || []).filter((p) => p.channel_id === channelFilter);

  const last24h = (raw.data || []).filter((m) => Date.now() - new Date(m.received_at || m.created_at).getTime() < 86_400_000);
  const parsedCount = last24h.filter((m) => m.parsed).length;
  const ignoredCount = last24h.length - parsedCount;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="card-surface p-4 flex items-center gap-6 flex-wrap flex-1" data-testid="message-log-summary">
          <p className="overline shrink-0">Last 24h</p>
          <span className="text-sm"><span className="mono font-bold">{last24h.length}</span> <span className="text-slate-500">messages captured</span></span>
          <span className="text-sm"><span className="mono font-bold text-emerald-600">{parsedCount}</span> <span className="text-slate-500">parsed into signals</span></span>
          <span className="text-sm"><span className="mono font-bold text-slate-400">{ignoredCount}</span> <span className="text-slate-500">commentary / ignored</span></span>
        </div>
        <Button
          variant="outline" size="sm" className="border-slate-300 shrink-0"
          data-testid="message-log-export"
          onClick={() => {
            const rows = [["received_at", "channel", "source", "parsed", "text"],
              ...visibleRaw.map((m) => [m.received_at || m.created_at, chById[resolveChannelId(m)]?.name || m.channel || "", m.source || "", m.parsed, (m.text || "").replace(/\n/g, " ").replace(/"/g, '""')])];
            const csv = rows.map((r) => r.map((v) => `"${v ?? ""}"`).join(",")).join("\n");
            const blob = new Blob([csv], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = "message_log.csv"; a.click();
            URL.revokeObjectURL(url);
          }}
        >
          <ListBullets size={16} className="mr-2" /> Export CSV
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap" data-testid="message-log-filters">
        {usedChannelIds.length > 1 && (
          <>
            <button
              onClick={() => setChannelFilter("all")}
              className={cn("px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors",
                channelFilter === "all" ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "bg-white text-slate-600 border-slate-200 hover:border-[#4F46E5]/40")}
            >
              All channels
            </button>
            {usedChannelIds.map((cid) => (
              <button
                key={cid}
                onClick={() => setChannelFilter(cid)}
                className={cn("px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors",
                  channelFilter === cid ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "bg-white text-slate-600 border-slate-200 hover:border-[#4F46E5]/40")}
              >
                {chById[cid]?.name || `#${cid.slice(0, 8)}`}
              </button>
            ))}
            <div className="w-px h-5 bg-slate-200 mx-1" />
          </>
        )}
        {[{ v: "all", label: "All sources" }, { v: "x", label: "𝕏 X / Twitter" }, { v: "telegram", label: "📡 Telegram" }].map((s) => (
          <button
            key={s.v}
            onClick={() => setSourceFilter(s.v)}
            data-testid={`message-log-source-${s.v}`}
            className={cn("px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors",
              sourceFilter === s.v ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "bg-white text-slate-600 border-slate-200 hover:border-[#4F46E5]/40")}
          >
            {s.label}
          </button>
        ))}
      </div>
      <p className="text-xs text-slate-400 text-center" data-testid="message-log-result-count">
        Showing {visibleRaw.length} of {(raw.data || []).length} messages
        {channelFilter !== "all" || sourceFilter !== "all" ? " (filtered)" : ""}
      </p>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <PendingAiApprovalCard />
      <div className="card-surface p-6" data-testid="section-raw-signals">
        <p className="overline">Raw Telegram messages</p>
        <h3 className="font-display text-xl mt-1">Message Log</h3>
        <p className="text-xs text-slate-500 mb-4">
          Every message the bot receives, verbatim — useful when a signal didn't
          parse the way you expected.
        </p>
        <ScrollArea className="h-[560px] pr-3">
          <div className="space-y-2">
            {visibleRaw.map((m) => (
              <div key={m.id} className="card-elevated p-3">
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span>{new Date(m.received_at || m.created_at).toLocaleString()}</span>
                  <span className="ml-auto">{m.source === "x_subscriber_scrape" ? "𝕏" : "📡 TG"}</span>
                  <Badge variant={m.parsed ? "info" : "muted"}>{m.parsed ? "parsed" : "unparsed"}</Badge>
                </div>
                <pre className="mono text-xs text-slate-800 whitespace-pre-wrap mt-2">{m.text}</pre>
                <div className="text-[10px] text-slate-500 mt-2">
                  channel: {chById[resolveChannelId(m)]?.name || m.channel || "—"}
                </div>
              </div>
            ))}
            {visibleRaw.length === 0 && <EmptyBox label="No raw messages match this filter" />}
          </div>
        </ScrollArea>
      </div>

      <div className="card-surface p-6" data-testid="section-parsed-signals">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="overline">Parsed structured signals</p>
            <h3 className="font-display text-xl mt-1">Parser Output</h3>
          </div>
          <ReparseStuckButton />
        </div>
        <ScrollArea className="h-[560px] pr-3">
          <div className="space-y-2">
            {visibleParsed.map((p) => (
              <div key={p.id} className="card-elevated p-3">
                <div className="flex items-center gap-2 text-xs">
                  <Badge variant="info">{p.action_type}</Badge>
                  {p.symbol && <Badge variant="muted">{p.symbol}</Badge>}
                  {p.side && <Badge variant={p.side === "long" ? "profit" : "loss"}>{p.side}</Badge>}
                  <Badge variant={p.parser_source === "ai" ? "warn" : p.parser_source === "correlated" ? "info" : "muted"}>
                    {p.parser_source === "ai" ? "🤖 AI-reviewed" : p.parser_source}
                  </Badge>
                  <span className="ml-auto mono text-[10px] text-slate-500">
                    {p.confidence != null ? `conf ${(p.confidence * 100).toFixed(0)}%` : ""}
                  </span>
                </div>
                {p.ai_reasoning && (
                  <div className="mt-2 p-2 rounded-md bg-amber-50 border border-amber-200"
                       data-testid={`ai-reasoning-${p.id}`}>
                    <p className="text-[10px] uppercase tracking-widest text-amber-600 mb-1">🤖 AI reasoning</p>
                    <p className="text-xs text-slate-800 italic">{p.ai_reasoning}</p>
                  </div>
                )}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3 text-xs mono">
                  <Kv k="Entry" v={p.entry_prices?.join(", ") || "—"} />
                  <Kv k="SL" v={p.stop_loss ?? "—"} />
                  <Kv k="TP" v={p.take_profits?.join(", ") || "—"} />
                  <Kv k="Lev" v={p.leverage ?? "—"} />
                  <Kv k="Partial" v={p.partial_percent ? `${p.partial_percent}%` : "—"} />
                  <Kv k="When" v={new Date(p.created_at).toLocaleTimeString()} />
                </div>
              </div>
            ))}
            {visibleParsed.length === 0 && <EmptyBox label="No parsed signals match this filter" />}
          </div>
        </ScrollArea>
      </div>
    </div>
    </div>
  );
}

/* ============ EVENTS ============ */
// Real trade_events event_type values grouped into the categories the
// Activity Log filters by. "Config changes" isn't included here - those are
// logged to system_logs (trade_engine source), not trade_events, and belong
// on the System Log side instead - see the categorization note below.
const EVENT_CATEGORIES = {
  entry: { label: "Entries", dot: "#059669", badgeBg: "#E7F7F1", badgeColor: "#059669",
    types: ["entry_placed", "limit_order_placed", "limit_order_filled", "ep1_filled_ep2_placed", "ep2_filled"] },
  close: { label: "Closes", dot: "#4F46E5", badgeBg: "#EEF0FF", badgeColor: "#4F46E5",
    types: ["closed", "tp1_hit_exchange", "tp2_hit_exchange", "tp3_hit_exchange", "tp1_hit", "tp2_hit", "tp3_hit",
            "partial_close_by_roi", "manual_close", "cancelled", "expired"] },
  risk: { label: "Risk / SL updates", dot: "#7C3AED", badgeBg: "#F3E8FF", badgeColor: "#7C3AED",
    types: ["sl_moved_to_be", "sl_to_breakeven", "tp_updated", "waiting_for_provider_sl", "manual_adjustment", "historical_correction"] },
  error: { label: "Errors", dot: "#DC2626", badgeBg: "#FDECEC", badgeColor: "#DC2626",
    types: ["entry_failed"] },
};
function categorizeEvent(eventType) {
  for (const [key, cat] of Object.entries(EVENT_CATEGORIES)) {
    if (cat.types.includes(eventType)) return { key, ...cat };
  }
  return { key: "other", label: "Other", dot: "#9CA0B8", badgeBg: "#F1F2F6", badgeColor: "#6B7089" };
}
function humanizeEventType(t) {
  return (t || "").replace(/_/g, " ").replace(/\bexchange\b/, "(exchange)");
}

function EventsSection() {
  const events = useQuery({ queryKey: ["events"], queryFn: async () => (await observability.events()).data, refetchInterval: 4000 });
  const logs = useQuery({ queryKey: ["logs"], queryFn: async () => (await observability.logs()).data, refetchInterval: 4000 });
  const [categoryFilter, setCategoryFilter] = useState("all");

  const evData = events.data || [];
  const last24h = evData.filter((e) => Date.now() - new Date(e.created_at).getTime() < 86_400_000);
  const counts = { entry: 0, close: 0, risk: 0, error: 0 };
  for (const e of last24h) {
    const cat = categorizeEvent(e.event_type).key;
    if (counts[cat] != null) counts[cat] += 1;
  }

  const visibleEvents = categoryFilter === "all" ? evData
    : evData.filter((e) => categorizeEvent(e.event_type).key === categoryFilter);

  return (
    <div className="space-y-4">
      <div className="card-surface p-4 flex items-center gap-6 flex-wrap" data-testid="activity-log-summary">
        <p className="overline shrink-0">Last 24h</p>
        {Object.entries(EVENT_CATEGORIES).map(([key, cat]) => (
          <span key={key} className="flex items-center gap-1.5 text-sm">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ background: cat.dot }} />
            <span className="mono font-bold">{counts[key]}</span>
            <span className="text-slate-500">{cat.label.toLowerCase()}</span>
          </span>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div>
          <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="activity-log-filter">
            <button
              onClick={() => setCategoryFilter("all")}
              className={cn("px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors",
                categoryFilter === "all" ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "bg-white text-slate-600 border-slate-200 hover:border-[#4F46E5]/40")}
            >
              All events
            </button>
            {Object.entries(EVENT_CATEGORIES).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => setCategoryFilter(key)}
                className={cn("px-3.5 py-1.5 rounded-full text-xs font-bold border transition-colors",
                  categoryFilter === key ? "bg-[#4F46E5] text-white border-[#4F46E5]" : "bg-white text-slate-600 border-slate-200 hover:border-[#4F46E5]/40")}
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="card-surface p-6" data-testid="section-events">
            <p className="overline">Trade lifecycle</p>
            <h3 className="font-display text-xl mt-1">Activity Log</h3>
            <p className="text-xs text-slate-500 mb-4">
              Every SL move, close, and error, in order — check here if a trade did
              something you didn't expect.
            </p>
            <ScrollArea className="h-[560px] pr-3">
              <div className="space-y-2">
                {visibleEvents.map((e) => {
                  const cat = categorizeEvent(e.event_type);
                  return (
                    <div key={e.id} className="flex gap-3 p-3 rounded-md" style={{ background: "var(--bg-elevated)" }}>
                      <span className="w-2 h-2 rounded-full mt-1.5 shrink-0" style={{ background: cat.dot }} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-xs flex-wrap">
                          <span className="text-xs font-bold capitalize">{humanizeEventType(e.event_type)}</span>
                          <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ background: cat.badgeBg, color: cat.badgeColor }}>
                            {cat.label}
                          </span>
                          <span className="ml-auto text-[10px] text-slate-500 shrink-0">{new Date(e.created_at).toLocaleString()}</span>
                        </div>
                        {e.detail && <p className="text-sm text-slate-900 mt-1">{e.detail}</p>}
                      </div>
                    </div>
                  );
                })}
                {visibleEvents.length === 0 && <EmptyBox label="No events match this filter" />}
              </div>
            </ScrollArea>
          </div>
          <p className="text-xs text-slate-400 text-center mt-3" data-testid="activity-log-result-count">
            Showing {visibleEvents.length} of {evData.length} events
            {categoryFilter !== "all" ? " (filtered)" : ""}
          </p>
        </div>

        <div className="card-surface p-6" data-testid="section-logs">
          <p className="overline">Errors & system events</p>
          <h3 className="font-display text-xl mt-1 mb-4">System Log</h3>
          <ScrollArea className="h-[560px] pr-3">
            <div className="space-y-2">
              {(logs.data || []).map((l) => (
                <div key={l.id} className="p-3 rounded-md" style={{ background: "var(--bg-elevated)" }}>
                  <div className="flex items-center gap-2 text-xs">
                    <Badge variant={l.level === "error" ? "loss" : l.level === "warn" ? "warn" : "muted"}>
                      {l.level}
                    </Badge>
                    <Badge variant="info">{l.source}</Badge>
                    <span className="ml-auto text-[10px] text-slate-500">{new Date(l.created_at).toLocaleString()}</span>
                  </div>
                  <p className="text-sm mt-1 text-slate-800">{l.message}</p>
                </div>
              ))}
              {(logs.data || []).length === 0 && <EmptyBox label="No system logs" />}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}

/* ============ Shared small components ============ */
function EmptyBox({ label }) {
  return (
    <div className="text-center py-16 text-slate-500 text-sm">
      <Warning size={28} className="mx-auto mb-2 opacity-40" />
      {label}
    </div>
  );
}


